import { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import MonitorButtonLoader from '@/components/shared/loader/monitor-button-loader';
import SidePanel from '@/components/shared/side-panel';
import StationContentRenderer from '@/components/stations/station-content-renderer';
import useStationSearch from '@/hooks/use-station-search';
import useStationsState from '@/hooks/use-stations-state';
import { INCIDENT_DETAILS, INCIDENTS_LIST, LIST, MONITOR, TAB_VALUES } from '@/utils/constants';
import { SEARCH_PARAM_VEREINA_STATUS } from '@/utils/constants/queries';

const LazyStationMonitor = lazy(() => import('@/components/station-details/station-monitor'));

const Stations = () => {
  const {
    viewType,
    isShowSidePanel,
    hasBackIcon,
    handleClose,
    handleBack,
    headerText,
    hasHeaderBottomBorder,
    passengerMonitorLink,
    rhbMonitorLink,
    hasStationDetailsError,
    isStationDetailsLoading,
    setHasStationDetailsError,
    setIsStationDetailsLoading,
    isFullscreen,
    currentStationId,
  } = useStationsState();

  const [selectedTab, setSelectedTab] = useState<string>(TAB_VALUES.DEPARTURES);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get(SEARCH_PARAM_VEREINA_STATUS)) {
      setSelectedTab(TAB_VALUES.DEPARTURES);
    }

    if (viewType === 'STATION_DETAILS') {
      setSelectedTab(TAB_VALUES.DEPARTURES);
    }
  }, [searchParams, viewType]);

  useStationSearch(viewType);

  const viewsWithoutFooter = [INCIDENT_DETAILS, MONITOR, INCIDENTS_LIST, LIST];
  const shouldIgnoreFooter = viewsWithoutFooter.includes(viewType) || hasStationDetailsError;

  const shouldHideFooter =
    shouldIgnoreFooter ||
    (selectedTab !== TAB_VALUES.ARRIVALS && selectedTab !== TAB_VALUES.DEPARTURES);

  const renderFooter = () => {
    if (shouldHideFooter) {
      return null;
    }

    if (isStationDetailsLoading) {
      return (
        <div className="py-3 mt-3 flex flex-col gap-2">
          <MonitorButtonLoader />
        </div>
      );
    }

    return (
      <Suspense fallback={<MonitorButtonLoader />}>
        <LazyStationMonitor isStationDetailsLoading={isStationDetailsLoading} />
      </Suspense>
    );
  };

  return (
    <SidePanel
      header={headerText}
      viewType={viewType}
      isSidePanelOpen={isShowSidePanel}
      setSidePanelOpen={handleClose}
      hasBackIcon={hasBackIcon}
      onClickBackIcon={handleBack}
      hasHeaderBottomBorder={hasHeaderBottomBorder}
      isFullscreen={isFullscreen}
      footer={renderFooter()}
    >
      <Fragment key={viewType}>
        <StationContentRenderer
          viewType={viewType}
          passengerMonitorLink={passengerMonitorLink}
          rhbMonitorLink={rhbMonitorLink}
          onStationDetailsError={setHasStationDetailsError}
          onStationDetailsLoading={setIsStationDetailsLoading}
          selectedTab={selectedTab}
          onTabSelect={setSelectedTab}
          currentStationId={currentStationId}
        />
      </Fragment>
    </SidePanel>
  );
};

export default Stations;
